import { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";

import {
  appendUserActionEntry,
  companyIdSelector,
  currentUserIdSelector,
  drawersAtom,
  DrawerType,
  pageEntityAtom,
  RydeUiUnit,
  sanitizeDrawerPropsForUserActionsHistory,
  USER_ACTIONS_HISTORY_EXCLUDED_DRAWER_TYPES,
  UserActionHistoryOrigin,
} from "@ryde/shared";

import {
  parseCrudRecordIdFromPath,
  resolveRecordedDrawerTitle,
} from "./user-actions-history.utils";

export const UserActionsHistoryRecorder: FC = () => {
  const location = useLocation();
  const pageEntity = useRecoilValue(pageEntityAtom);
  const userId = useRecoilValue(currentUserIdSelector);
  const companyId = useRecoilValue(companyIdSelector);
  const drawers = useRecoilValue(drawersAtom);
  const prevDrawerCount = useRef(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (!userId || !companyId) {
      return;
    }
    if (drawers.length > prevDrawerCount.current) {
      const added = drawers.slice(prevDrawerCount.current);
      for (const { drawerProps } of added) {
        const type = drawerProps.type;
        if (USER_ACTIONS_HISTORY_EXCLUDED_DRAWER_TYPES.has(type)) {
          continue;
        }
        const rawUnknown = drawerProps as unknown as Record<string, unknown>;
        const { type: _tp, drawerNumber: _dn, ...rest } = rawUnknown;
        const drawerPayload =
          sanitizeDrawerPropsForUserActionsHistory(rest) ?? {};
        const title = resolveRecordedDrawerTitle(type, drawerPayload, t);
        appendUserActionEntry({
          userId,
          companyId,
          partial: {
            origin: UserActionHistoryOrigin.Drawer,
            title,
            pathname: window.location.pathname,
            search: window.location.search,
            drawerType: type,
            drawerPayload:
              Object.keys(drawerPayload).length > 0 ? drawerPayload : undefined,
          },
        });
      }
    }
    prevDrawerCount.current = drawers.length;
  }, [companyId, drawers, t, userId]);

  useEffect(() => {
    if (!userId || !companyId) {
      return;
    }
    if (pageEntity === "unknown") {
      return;
    }
    const recordId = parseCrudRecordIdFromPath(location.pathname);
    if (recordId === undefined) {
      return;
    }
    const entity = pageEntity as RydeUiUnit;
    const title = resolveRecordedDrawerTitle(
      DrawerType.EditForm,
      { entity, id: recordId },
      t,
    );
    appendUserActionEntry({
      userId,
      companyId,
      partial: {
        origin: UserActionHistoryOrigin.RouteCard,
        title,
        pathname: location.pathname,
        search: location.search ?? "",
        drawerType: DrawerType.EditForm,
        drawerPayload: { entity, id: recordId },
      },
    });
  }, [companyId, location.pathname, location.search, pageEntity, t, userId]);

  return null;
};
